import React, { useRef, useEffect, useState, useLayoutEffect } from 'react';
import { ScoreBoard } from './ScoreBoard';
import { BetBox } from './BetBox';
import { isSkyxNewRoute } from '@core/utils/gameRouteSlugs';
import { isHandheldLandscape } from '@core/utils/handheldLandscape';

interface MobileGameContainerProps {
  controller: any;
  /** Game-specific bet controls (core must not import from @games). */
  renderBetControls?: (ctx: {
    deviceType: 'desktop' | 'tablet' | 'mobile';
    containerStyle: React.CSSProperties;
  }) => React.ReactNode;
}

// IDs of external DOM elements that must scroll in sync with the container
const SYNC_TARGET_IDS = [
  'stage-board',
  'game-canvas',
  'multiplier-display',
  'waiting-modal',
  'history-component-root',
];

export const MobileGameContainer: React.FC<MobileGameContainerProps> = ({
  controller,
  renderBetControls,
}) => {
  const usesCompactPurpleProfile =
    controller?.getPlatformUIConfig?.()?.profile === 'compactPurple';
  const isSkyxNew = isSkyxNewRoute(controller?.getGameSlug?.());
  const containerRef = useRef<HTMLDivElement>(null);
  const spacerRef = useRef<HTMLDivElement>(null);
  const scoreboardRef = useRef<HTMLDivElement>(null);

  // Cache the external DOM elements for landscape sync
  const syncedTargetsRef = useRef<HTMLElement[]>([]);

  const gameState = controller.getGameState();
  const deviceType = gameState.deviceType;
  const isLandscape = gameState.isLandscape;

  const ua = navigator.userAgent.toLowerCase();

  // Detect real phones by User-Agent
  const isPhoneUA =
    ua.includes('iphone') || (ua.includes('android') && ua.includes('mobile'));

  const skyxHandheldLandscapeScroll =
    isSkyxNew && isHandheldLandscape(deviceType, isLandscape);

  // skyx_new handheld landscape keeps the standard below-canvas layout (no overlay
  // spacer) so the bet panel never stacks on top of the game canvas.
  const isMobileLandscape = isPhoneUA && isLandscape;

  // State from Version 2
  const [, setIsAtScoreboard] = useState(false);

  // --- 1. CACHE TARGETS (Source 1 Logic - Landscape Only) ---
  useEffect(() => {
    if (deviceType === 'desktop') return;
    if (!isMobileLandscape) {
      syncedTargetsRef.current = [];
      return;
    }

    const allTargets = SYNC_TARGET_IDS.map(id =>
      document.getElementById(id)
    ).filter((el): el is HTMLElement => !!el);

    // Filter out nested targets to prevent double-movement
    const rootTargets = allTargets.filter(target => {
      const isChildOfAnotherTarget = allTargets.some(
        parent => parent !== target && parent.contains(target)
      );
      return !isChildOfAnotherTarget;
    });

    // Initialize styles for hardware acceleration
    rootTargets.forEach(el => {
      el.style.willChange = 'transform';
      // Safari Fix: Force hardware acceleration to prevent flickering/hiding
      el.style.transform = `translate3d(0, -${containerRef.current?.scrollTop || 0}px, 0)`;
    });

    syncedTargetsRef.current = rootTargets;

    return () => {
      rootTargets.forEach(el => {
        el.style.willChange = '';
        el.style.transform = '';
        el.style.transition = '';
      });
    };
  }, [isMobileLandscape, deviceType]);

  // --- 2. SCROLL SYNCHRONIZATION (Source 1 Logic - Landscape Only) ---
  useLayoutEffect(() => {
    if (!isMobileLandscape) return;

    const container = containerRef.current;
    if (!container) return;

    let rafId: number;

    const handleScrollSync = () => {
      const scrollTop = container.scrollTop;
      const transformValue = `translate3d(0, -${scrollTop}px, 0)`;

      const targets = syncedTargetsRef.current;
      for (let i = 0; i < targets.length; i++) {
        const el = targets[i];
        if (el.style.transition !== 'none') {
          el.style.transition = 'none';
        }
        el.style.transform = transformValue;
      }
    };

    // Hide native elements that might overlap incorrectly during this mode
    const multiplierDisplay = document.getElementById('multiplier-display');
    const waitingModal = document.getElementById('waiting-modal');

    // Manual overrides for landscape mode
    if (multiplierDisplay) {
      multiplierDisplay.style.top = '-42px';
    }
    if (waitingModal) waitingModal.style.top = '-42px';

    const onScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(handleScrollSync);
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    handleScrollSync();

    return () => {
      container.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(rafId);

      if (multiplierDisplay) {
        multiplierDisplay.style.top = '';
      }
      if (waitingModal) waitingModal.style.top = '';

      container.scrollTop = 0;
      syncedTargetsRef.current.forEach(el => {
        el.style.willChange = '';
        el.style.transform = '';
        el.style.transition = '';
      });
      syncedTargetsRef.current = [];
    };
  }, [isMobileLandscape]);

  // skyx_new handheld landscape: body uses touch-action:none so iOS won't natively
  // scroll overflow containers. Forward vertical pans to the main container or nested
  // scoreboard list (.bet-list-scrollbar). Header stays interactive-only.
  useEffect(() => {
    if (!skyxHandheldLandscapeScroll) return;

    const container = containerRef.current;
    if (!container) return;

    const SCROLL_THRESHOLD_PX = 6;
    const NESTED_SCROLL_SELECTOR = '.bet-list-scrollbar';

    const pan = {
      active: false,
      decided: false,
      startY: 0,
      startX: 0,
      startScrollTop: 0,
      touchId: -1,
      scrollTarget: null as HTMLElement | null,
    };

    const getMaxScroll = (el: HTMLElement) =>
      Math.max(0, el.scrollHeight - el.clientHeight);

    const isBlockedTarget = (target: EventTarget | null): boolean => {
      if (!target || !(target instanceof Element)) return true;
      return !!target.closest(
        '#game-header, [data-skyx-chat], .autoplay-popup, [data-autoplay-modal], [aria-modal="true"], [role="dialog"], button, a, input, textarea, select, label, [contenteditable="true"]'
      );
    };

    const isScrollZone = (target: Element): boolean => {
      return !!target.closest(
        '.mobile-game-container, .play-board-wrapper, #stage-board, #game-canvas, #history, #history-component-root, #multiplier-display, .mobile-scoreboard-container, .main_bets_widget, .bets-widget-container, .bet-list-scrollbar'
      );
    };

    const resolveScrollTarget = (target: Element): HTMLElement | null => {
      if (isBlockedTarget(target)) return null;

      const nested = target.closest(NESTED_SCROLL_SELECTOR);
      if (nested instanceof HTMLElement && getMaxScroll(nested) > 0) {
        return nested;
      }

      if (isScrollZone(target)) {
        return container;
      }

      return null;
    };

    const applyScrollTop = (value: number) => {
      const el = pan.scrollTarget;
      if (!el) return;

      if (el === container) {
        const maxScroll = getMaxScroll(el);
        el.scrollTop = Math.max(0, Math.min(value, maxScroll));
        return;
      }

      const maxNested = getMaxScroll(el);
      const nestedNext = Math.max(0, Math.min(value, maxNested));
      el.scrollTop = nestedNext;

      const spill = value - nestedNext;
      if (spill !== 0) {
        const outerNext = container.scrollTop + spill;
        const maxOuter = getMaxScroll(container);
        container.scrollTop = Math.max(0, Math.min(outerNext, maxOuter));
      }
    };

    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 1) {
        pan.active = false;
        pan.scrollTarget = null;
        return;
      }

      const target = event.target;
      if (!(target instanceof Element)) {
        pan.active = false;
        return;
      }

      const scrollTarget = resolveScrollTarget(target);
      if (!scrollTarget) {
        pan.active = false;
        pan.scrollTarget = null;
        return;
      }

      const touch = event.touches[0];
      pan.active = true;
      pan.decided = false;
      pan.scrollTarget = scrollTarget;
      pan.touchId = touch.identifier;
      pan.startY = touch.clientY;
      pan.startX = touch.clientX;
      pan.startScrollTop = scrollTarget.scrollTop;
    };

    const onTouchMove = (event: TouchEvent) => {
      if (!pan.active || !pan.scrollTarget) return;

      const touch = Array.from(event.touches).find(
        t => t.identifier === pan.touchId
      );
      if (!touch) return;

      const deltaY = pan.startY - touch.clientY;
      const deltaX = pan.startX - touch.clientX;

      if (!pan.decided) {
        if (
          Math.abs(deltaY) < SCROLL_THRESHOLD_PX &&
          Math.abs(deltaX) < SCROLL_THRESHOLD_PX
        ) {
          return;
        }
        pan.decided = true;
        if (Math.abs(deltaY) <= Math.abs(deltaX)) {
          pan.active = false;
          return;
        }
      }

      applyScrollTop(pan.startScrollTop + deltaY);
      if (event.cancelable) {
        event.preventDefault();
      }
    };

    const endPan = () => {
      pan.active = false;
      pan.decided = false;
      pan.touchId = -1;
      pan.scrollTarget = null;
    };

    const captureOpts: AddEventListenerOptions = {
      passive: false,
      capture: true,
    };
    const startOpts: AddEventListenerOptions = { passive: true, capture: true };
    document.addEventListener('touchstart', onTouchStart, startOpts);
    document.addEventListener('touchmove', onTouchMove, captureOpts);
    document.addEventListener('touchend', endPan, { capture: true });
    document.addEventListener('touchcancel', endPan, { capture: true });

    return () => {
      document.removeEventListener('touchstart', onTouchStart, startOpts);
      document.removeEventListener('touchmove', onTouchMove, captureOpts);
      document.removeEventListener('touchend', endPan, { capture: true });
      document.removeEventListener('touchcancel', endPan, { capture: true });
    };
  }, [skyxHandheldLandscapeScroll]);

  // --- 3. STANDARD MOBILE SCROLL CHECK (Source 2 Logic) ---
  useEffect(() => {
    if (deviceType === 'desktop' || isMobileLandscape) return;

    const container = containerRef.current;
    const scoreboard = scoreboardRef.current;
    if (!container || !scoreboard) return;

    const handleScroll = () => {
      const scoreboardTop = scoreboard.offsetTop - container.scrollTop;
      const isAtScoreboard = Math.abs(scoreboardTop) < 10;
      setIsAtScoreboard(isAtScoreboard);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [deviceType, isMobileLandscape]);

  // --- STYLES ---

  const landscapeHeaderOffset = 'var(--headerHeightDynamic, 40px)';

  /**
   * Baseline single-row header height. Stacked UKGC session timers grow
   * `--headerHeightDynamic` above this; add that delta so bets/scoreboard
   * shift down with the game board (portrait only — landscape uses the
   * offset path below).
   */
  const stackedSessionHeaderOffset =
    'max(0px, calc(var(--headerHeightDynamic, 40px) - 40px))';

  const standardMobileTop = `calc(max(272px, var(--headerHeightDynamic, 40px) + var(--resultHistoryHeight, 32px) + (100svh - (var(--headerHeightDynamic, 40px) + var(--paddingSmall, 4px) + var(--resultHistoryHeight, 32px) + var(--marginSmall, 4px) + var(--paddingSmall, 4px) + var(--betControlsHeight, 292px) + var(--paddingSmall, 4px) + var(--paddingMedium, 8px) + var(--navigationSwitcherHeight, 32px) + var(--marginSmall, 4px) + var(--totalWinWidgetHeight, 68px) + var(--marginSmall, 4px)))) + ${stackedSessionHeaderOffset})`;

  const containerStyle: React.CSSProperties = {
    position: 'fixed',
    top: isMobileLandscape ? landscapeHeaderOffset : standardMobileTop,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
    zIndex: isMobileLandscape ? 5 : 1000,
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(255, 255, 255, 0.3) transparent',
    background:
      'linear-gradient(180deg, rgba(0, 0, 0, 0.1) 0%, transparent 20px)',
    paddingTop: 0,
    // Overlay landscape (non-skyx phones): header receives taps; bet children use auto.
    // skyx handheld landscape uses standard layout with full container scroll.
    pointerEvents: isMobileLandscape ? 'none' : 'auto',
    transform: 'translateZ(0)',
    ...(skyxHandheldLandscapeScroll
      ? {
          touchAction: 'pan-y',
          overscrollBehaviorY: 'contain',
          overflowY: 'scroll',
        }
      : {}),
  };

  const landscapeSpacerStyle: React.CSSProperties = {
    width: '100%',
    height: 'calc(100dvh - 190px)',
    minHeight: '250px',
    flexShrink: 0,
    background: 'transparent',
    order: 0,
    pointerEvents: 'none',
  };

  const isMobilePortrait = deviceType === 'mobile' && !isMobileLandscape;

  const betBoxContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: isMobilePortrait ? 'column' : 'row',
    justifyContent: 'center',
    alignItems: isMobilePortrait ? 'stretch' : undefined,
    flexShrink: '0',
    padding:
      isSkyxNew && (deviceType === 'mobile' || deviceType === 'tablet')
        ? '0'
        : '1px 3px',
    width: '100%',
    minHeight: deviceType === 'tablet' ? '' : '292px',
    flexWrap:
      deviceType === 'tablet'
        ? undefined
        : isMobilePortrait
          ? 'nowrap'
          : 'wrap',
    gap: isSkyxNew
      ? '0'
      : deviceType === 'tablet'
        ? '2px'
        : isMobilePortrait
          ? '4px'
          : '',
    order: 1,
    marginTop: undefined,
    backgroundColor: usesCompactPurpleProfile
      ? '#170550'
      : isMobileLandscape
        ? '#0e0e0e'
        : 'transparent',
    zIndex: 2,
    position: 'relative',
    pointerEvents: 'auto',
    ...(skyxHandheldLandscapeScroll ? { touchAction: 'pan-y' } : {}),
  };

  const scoreboardWrapperStyle: React.CSSProperties = {
    flex: '0 0 auto',
    width: '100%',
    position: 'relative',
    height: 'auto',
    minHeight: 0,
    zIndex: 2,
    order: 2,
    top: deviceType === 'mobile' ? '-1px' : '',
    marginTop:
      isMobilePortrait && usesCompactPurpleProfile ? '-6px' : undefined,
    padding:
      isMobilePortrait && usesCompactPurpleProfile
        ? isSkyxNew
          ? '0 8px max(12px, env(safe-area-inset-bottom, 0px))'
          : '0 4px 1px'
        : deviceType === 'tablet' && usesCompactPurpleProfile && isSkyxNew
          ? '1px 8px max(10px, env(safe-area-inset-bottom, 0px))'
          : '1px 4px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    backgroundColor: usesCompactPurpleProfile
      ? '#170550'
      : isMobileLandscape
        ? '#0e0e0e'
        : 'transparent',
    pointerEvents: 'auto',
    ...(skyxHandheldLandscapeScroll ? { touchAction: 'pan-y' } : {}),
  };

  if (deviceType === 'desktop') {
    return null;
  }

  return (
    <div
      ref={containerRef}
      style={containerStyle}
      className={
        skyxHandheldLandscapeScroll
          ? 'mobile-game-container skyx-handheld-landscape-scroll'
          : 'mobile-game-container'
      }
    >
      {isMobileLandscape && (
        <div ref={spacerRef} style={landscapeSpacerStyle} />
      )}

      <div style={betBoxContainerStyle} className="mobile-betbox-container">
        {renderBetControls ? (
          renderBetControls({
            deviceType,
            containerStyle: betBoxContainerStyle,
          })
        ) : (
          <>
            <BetBox side="A" controller={controller} />
            <BetBox side="B" controller={controller} />
          </>
        )}
      </div>

      <div
        ref={scoreboardRef}
        style={scoreboardWrapperStyle}
        className="mobile-scoreboard-container"
      >
        <ScoreBoard controller={controller} />
      </div>
    </div>
  );
};
